import { frozenEvaluationRequestSchema } from "./frozen-evaluation.js";
import { z } from "zod";
import { v2HybridChannelCardSchema } from "./v2-hybrid-capabilities.js";
import { accessExecutionGrantV1Schema, accessPolicyModeSchema, approvalDecisionAssertionV1Schema, } from "./access-control.js";
import { externalClientAppSchema } from "./client-attribution.js";
import { taskRunSummarySchema, taskRunMessagePartSchema, taskRunMessageStatusSchema, } from "./task-runs.js";
import { governedPromptProfileSchema, openAICodexExperimentSchema, taskAgentStepReasoningEnabledSchema, taskAgentCacheOptimizationEnabledSchema, sessionRoutingArchitectureSchema, taskRunCapabilitySchema, taskRunExecutionTierSchema, } from "./primitives.js";
import { runtimePluginRefSchema, runtimePluginSchema, runtimePluginSkillOverlaySchema, } from "./runtime-plugins.js";
import { runModelConfigSchema } from "./model-config.js";
import { agentPromptSurfaceSchema } from "./agent-prompts.js";
import { taskVisibilitySchema } from "./tasks.js";
import { resolverEvalModeSchema } from "./resolver-eval.js";
import { selfHostedExperimentModeSchema } from "./self-hosted-experiment.js";
import { researchWarehouseProfileSchema } from "./research-warehouse.js";
import { activeSessionContractProjectionSchema } from "./session-contracts.js";
import { memoryRolloutModeSchema, memoryRunPolicySchema, } from "./memory-policy.js";
import { intentionalMemoryDigestPacketSchema } from "./memory-packet.js";
export const sessionIdSchema = z.string().trim().min(1);
// Read compatibility only: historical sessions may still contain "worker".
// New session/run creation is Electric-only.
export const sessionRuntimeBackendSchema = z.enum(["worker", "electric"]);
export const sessionStatusSchema = z.enum([
    "queued",
    "running",
    "paused",
    "waiting_for_input",
    "cancelling",
    "cancelled",
    "completed",
    "failed",
]);
export const sessionCapabilityModeSchema = z.enum([
    "tenant_policy",
    "explicit",
    "resolver_eval",
]);
export const sessionInputMessageSchema = z.object({
    role: z.enum(["system", "user", "assistant", "tool"]),
    content: z.string(),
    name: z.string().trim().min(1).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const sessionInputSnapshotSchema = z.object({
    title: z.string().trim().min(1).optional(),
    messages: z.array(sessionInputMessageSchema).min(1),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
/*
 * Structured per-turn context (audit M2.3): clients reference workspace
 * resources by id; the control plane resolves them server-side when the
 * turn is queued, so resolved context is recorded with the run and clients
 * never inline stale copies into the prompt.
 */
export const workspaceCreationKindSchema = z.enum(["document", "sheet", "dataset", "view", "workflow"]);
export const sessionTurnContextSchema = z.object({
    creationKind: workspaceCreationKindSchema.optional(),
    channelId: z.string().trim().min(1).optional(),
    dataSetId: z.string().trim().min(1).optional(),
    viewId: z.string().trim().min(1).optional(),
    sessionId: z.string().trim().min(1).optional(),
    throughRunId: z.string().trim().min(1).optional(),
    includeTranscript: z.boolean().default(false),
    recordIds: z.array(z.string().trim().min(1)).max(50).default([]),
    fileIds: z.array(z.string().trim().min(1)).max(50).default([]),
    inboxItemIds: z.array(z.string().trim().min(1)).max(50).default([]),
    inboxClips: z.array(z.object({
        inboxItemId: z.string().trim().min(1),
        text: z.string().trim().min(1).max(4_000),
    }).strict()).max(20).default([]),
    // Twin entities the user asked about; each turn's context carries their cards.
    entityIds: z.array(z.string().trim().min(1)).max(10).default([]),
}).superRefine((context, refinement) => {
    if (context.viewId && !context.channelId) {
        refinement.addIssue({
            code: "custom",
            path: ["viewId"],
            message: "viewId requires channelId",
        });
    }
    if (context.dataSetId && !context.channelId) {
        refinement.addIssue({
            code: "custom",
            path: ["dataSetId"],
            message: "dataSetId requires channelId",
        });
    }
    if (context.throughRunId && !context.sessionId) {
        refinement.addIssue({
            code: "custom",
            path: ["throughRunId"],
            message: "throughRunId requires sessionId",
        });
    }
    if (context.throughRunId && !context.includeTranscript) {
        refinement.addIssue({
            code: "custom",
            path: ["throughRunId"],
            message: "throughRunId requires includeTranscript",
        });
    }
});
const clientReferenceIdSchema = z.string().trim().min(1).max(200);
const clientInboxClipSchema = z.object({
    inboxItemId: clientReferenceIdSchema,
    text: z.string().trim().min(1).max(4_000),
}).strict();
/*
 * Thin-client session commands. Browser/native clients send only user intent
 * and stable MLX resource references; control-plane expands these into the
 * internal session/run envelope, prompt context, routing, model, and capability
 * policy.
 */
export const clientSessionReferencesSchema = z.object({
    creationKind: workspaceCreationKindSchema.optional(),
    channelId: clientReferenceIdSchema.optional(),
    dataSetId: clientReferenceIdSchema.optional(),
    viewId: clientReferenceIdSchema.optional(),
    sourceSessionId: clientReferenceIdSchema.optional(),
    sourceRunId: clientReferenceIdSchema.optional(),
    recordIds: z.array(clientReferenceIdSchema).max(50).default([]),
    fileIds: z.array(clientReferenceIdSchema).max(50).default([]),
    inboxItemIds: z.array(clientReferenceIdSchema).max(50).default([]),
    inboxClips: z.array(clientInboxClipSchema).max(20).default([]),
    entityIds: z.array(clientReferenceIdSchema).max(10).default([]),
}).strict().superRefine((references, refinement) => {
    if (references.viewId && !references.channelId) {
        refinement.addIssue({
            code: "custom",
            path: ["viewId"],
            message: "viewId requires channelId",
        });
    }
    if (references.dataSetId && !references.channelId) {
        refinement.addIssue({
            code: "custom",
            path: ["dataSetId"],
            message: "dataSetId requires channelId",
        });
    }
    if (references.sourceRunId && !references.sourceSessionId) {
        refinement.addIssue({
            code: "custom",
            path: ["sourceRunId"],
            message: "sourceRunId requires sourceSessionId",
        });
    }
});
export const clientSessionIntentSchema = z.object({
    text: z.string().trim().min(1).max(100_000),
}).strict();
export const clientKindSchema = z.enum(["web", "macos", "ios", "cli", "api", "mcp"]);
export const startClientSessionInputSchema = z.object({
    clientKind: clientKindSchema.default("web"),
    clientApp: externalClientAppSchema.optional(),
    clientLocalId: clientReferenceIdSchema.optional(),
    visibility: taskVisibilitySchema.default("private"),
    intent: clientSessionIntentSchema,
    references: clientSessionReferencesSchema.default({
        recordIds: [],
        fileIds: [],
        inboxItemIds: [],
        inboxClips: [],
        entityIds: [],
    }),
    modelConfig: runModelConfigSchema.optional(),
    warehouseProfile: researchWarehouseProfileSchema.optional(),
    requestedCapabilities: z.array(taskRunCapabilitySchema).min(1).optional(),
    requestedPluginSlugs: z.array(z.string().trim().min(1).max(120)).length(1).optional(),
    routingArchitecture: sessionRoutingArchitectureSchema.optional(),
    governedPromptProfile: governedPromptProfileSchema.optional(),
    openAICodexExperiment: openAICodexExperimentSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
    experimentalMode: z.union([
        resolverEvalModeSchema,
        selfHostedExperimentModeSchema,
    ]).optional(),
}).strict();
/**
 * What a follow-up does when the session is still working on a turn: refuse
 * (the default), or wait its turn. A queued message is a run in its own
 * right; the executor claims a session's turns in order.
 */
export const sessionMessageWhenBusySchema = z.enum(["reject", "queue"]);
export const sendClientSessionMessageInputSchema = z.object({
    sessionId: sessionIdSchema,
    clientKind: clientKindSchema.default("web"),
    clientApp: externalClientAppSchema.optional(),
    clientLocalId: clientReferenceIdSchema.optional(),
    intent: clientSessionIntentSchema,
    references: clientSessionReferencesSchema.default({
        recordIds: [],
        fileIds: [],
        inboxItemIds: [],
        inboxClips: [],
        entityIds: [],
    }),
    modelConfig: runModelConfigSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
    whenBusy: sessionMessageWhenBusySchema.optional(),
}).strict();
export const createSessionInputSchema = z.object({
    frozenEvaluation: frozenEvaluationRequestSchema.optional(),
    externalTaskId: z.string().trim().min(1).optional(),
    organisationId: z.string().trim().min(1).optional(),
    visibility: taskVisibilitySchema.default("private"),
    // Optional (audit M2.4): verified-subject auth attributes the session to
    // the real user; API-key callers fall back to a key-derived label.
    userId: z.string().trim().min(1).optional(),
    inputSnapshot: sessionInputSnapshotSchema,
    requestedPluginSlugs: z
        .array(z.string().trim().min(1).max(120))
        .max(32)
        .default([]),
    requestedSkillSlugs: z
        .array(z.string().trim().min(1).max(120))
        .max(32)
        .default([]),
    requestedCapabilities: z
        .array(taskRunCapabilitySchema)
        .min(1)
        .default(["readonly-data", "writeback"]),
    context: sessionTurnContextSchema.optional(),
    modelConfig: runModelConfigSchema.optional(),
    warehouseProfile: researchWarehouseProfileSchema.optional(),
    routingArchitecture: sessionRoutingArchitectureSchema.optional(),
    governedPromptProfile: governedPromptProfileSchema.optional(),
    openAICodexExperiment: openAICodexExperimentSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.optional(),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.optional(),
});
export const createSessionResponseSchema = z.object({
    sessionId: sessionIdSchema,
    taskId: z.string().trim().min(1).optional(),
    runId: z.string().trim().min(1).optional(),
    status: sessionStatusSchema,
});
const sendSessionMessageBodySchema = z.object({
    sessionId: sessionIdSchema,
    runId: z.string().trim().min(1).optional(),
    runRole: z.enum(["task_agent", "retrieval_agent"]).default("task_agent"),
    routingArchitecture: sessionRoutingArchitectureSchema.default("legacy_router"),
    governedPromptProfile: governedPromptProfileSchema.default("fat"),
    openAICodexExperiment: openAICodexExperimentSchema.optional(),
    taskAgentStepReasoningEnabled: taskAgentStepReasoningEnabledSchema.default(false),
    taskAgentCacheOptimizationEnabled: taskAgentCacheOptimizationEnabledSchema.default(false),
    parentRunId: z.string().trim().min(1).optional(),
    organisationId: z.string().trim().min(1).optional(),
    message: sessionInputMessageSchema.extend({
        role: z.enum(["user"]),
    }),
    context: sessionTurnContextSchema.optional(),
    whenBusy: sessionMessageWhenBusySchema.default("reject"),
    /** Execution handoff only: earlier turns a rollback took out of the conversation. */
    excludedConversationRunIds: z.array(z.string().trim().min(1)).max(500).optional(),
    requestedPluginSlugs: z.array(z.string().trim().min(1)).default([]),
    requestedSkillSlugs: z.array(z.string().trim().min(1)).default([]),
    pluginSkillOverlays: z
        .array(runtimePluginSkillOverlaySchema)
        .default([]),
    organizationStyle: z.string().max(500).optional(),
    runtimeChannelCards: z.array(v2HybridChannelCardSchema).max(500).optional(),
    runtimePlugins: z.array(runtimePluginSchema).optional(),
    availableRuntimePlugins: z.array(runtimePluginRefSchema).optional(),
    pendingRuntimePlugins: z.array(runtimePluginRefSchema).optional(),
    externalServicesConnected: z.boolean().optional(),
    channelWidgetsEnabled: z.boolean().optional(),
    agentPromptSurfaces: z.array(agentPromptSurfaceSchema).default([]),
    compiledPromptContext: z.string().optional(),
    memoryDigest: z.string().optional(),
    memoryDigestPacket: intentionalMemoryDigestPacketSchema.optional(),
    memoryPolicy: memoryRunPolicySchema.optional(),
    memoryRolloutMode: memoryRolloutModeSchema.optional(),
    memoryProtocolVersion: z.literal(1).optional(),
    channelAccessBypass: z.literal(false).default(false),
    attachedChannelId: z.string().trim().min(1).optional(),
    attachedViewId: z.string().trim().min(1).optional(),
    executionTier: taskRunExecutionTierSchema.optional(),
    warehouseProfile: researchWarehouseProfileSchema.optional(),
    sessionContract: activeSessionContractProjectionSchema.optional(),
    workerLease: z.object({
        workerId: z.string().trim().min(1).max(200),
        attemptNumber: z.number().int().positive(),
    }).strict().optional(),
    accessPolicyMode: accessPolicyModeSchema.optional(),
    executionGrants: z.array(accessExecutionGrantV1Schema).optional(),
    workflowSample: z.boolean().optional(),
    workflowDurable: z.boolean().optional(),
    runtimeCanaryProtocolVersion: z.literal(1).optional(),
    requestedCapabilities: z
        .array(taskRunCapabilitySchema)
        .min(1)
        .default(["readonly-data", "writeback"]),
    modelConfig: runModelConfigSchema.optional(),
}).superRefine((input, refinement) => {
    if (input.attachedViewId && !input.attachedChannelId) {
        refinement.addIssue({
            code: "custom",
            path: ["attachedViewId"],
            message: "attachedViewId requires attachedChannelId",
        });
    }
});
function rejectServerOwnedResolverEval(value, context) {
    if (value && typeof value === "object" && !Array.isArray(value) &&
        Object.prototype.hasOwnProperty.call(value, "resolverEval")) {
        context.addIssue({ code: "custom", message: "resolverEval is server-owned", path: ["resolverEval"] });
    }
    return value;
}
export const sendSessionMessageInputSchema = z.preprocess(rejectServerOwnedResolverEval, sendSessionMessageBodySchema);
/** Strict control-plane/worker execution handoff; never defaults authority. */
export const sendExecutionSessionMessageInputSchema = z.preprocess(rejectServerOwnedResolverEval, sendSessionMessageBodySchema.and(z.object({
    routingArchitecture: sessionRoutingArchitectureSchema,
    userId: z.string().trim().min(1),
    verifiedSubjectId: z.string().trim().min(1).optional(),
    pluginSubjectId: z.string().trim().min(1).optional(),
    attachedChannelAdminAccess: z.boolean().default(false),
    accessPolicyMode: accessPolicyModeSchema,
}).and(z.discriminatedUnion("accessPolicyMode", [
    z.object({
        accessPolicyMode: z.literal("legacy"),
        executionGrants: z.array(accessExecutionGrantV1Schema).max(0),
    }),
    z.object({
        accessPolicyMode: z.enum(["shadow", "warn", "enforce"]),
        executionGrants: z.array(accessExecutionGrantV1Schema).min(1),
    }),
]))).superRefine((input, refinement) => {
    if (input.attachedChannelAdminAccess && !input.attachedChannelId) {
        refinement.addIssue({
            code: "custom",
            path: ["attachedChannelAdminAccess"],
            message: "Attached-channel admin access requires an attached channel",
        });
    }
}));
export const sessionMutationResponseSchema = z.object({
    ok: z.boolean(),
});
export const pauseSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
    reason: z.string().trim().min(1).optional(),
});
export const resumeSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
    resumeKey: z.string().trim().min(1).optional(),
    message: sessionInputMessageSchema
        .extend({
        role: z.enum(["user"]),
    })
        .optional(),
});
export const sessionSummarySchema = z.object({
    id: sessionIdSchema,
    tenantSlug: z.string().optional(),
    organisationId: z.string().optional(),
    userId: z.string(),
    verifiedSubjectId: z.string().optional(),
    pluginSubjectId: z.string().optional(),
    externalTaskId: z.string().optional(),
    visibility: taskVisibilitySchema.optional(),
    channelId: z.string().trim().min(1).optional(),
    title: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    status: sessionStatusSchema,
    runtimeBackend: sessionRuntimeBackendSchema,
    requestedPluginSlugs: z.array(z.string()),
    resolvedPluginSlugs: z.array(z.string()),
    requestedSkillSlugs: z.array(z.string()),
    resolvedSkillSlugs: z.array(z.string()),
    resolvedPluginSkillOverlays: z
        .array(runtimePluginSkillOverlaySchema)
        .default([]),
    requestedCapabilities: z.array(taskRunCapabilitySchema),
    // Rolling deployments may briefly read summaries from a server version
    // that predates persisted capability intent. Treat that legacy shape as
    // explicit authority rather than widening it.
    capabilityMode: sessionCapabilityModeSchema.default("explicit"),
    createdAt: z.number(),
    updatedAt: z.number(),
    startedAt: z.number().optional(),
    finishedAt: z.number().optional(),
});
export const listSessionsParamsSchema = z.object({
    status: z.array(sessionStatusSchema).max(sessionStatusSchema.options.length).default([]),
    owner: z.literal("me").optional(),
    organisationId: z.string().trim().min(1).optional(),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    includeMessages: z.coerce.boolean().default(false),
    fields: z.enum(["full", "summary"]).default("full"),
    /** "exclude" lists conversations only: a session a workflow started for one of its runs reads under the workflow. */
    workflowRuns: z.enum(["include", "exclude"]).default("include"),
});
export const listSessionsResponseSchema = z.object({
    sessions: z.array(sessionSummarySchema.extend({
        messages: z.array(z.lazy(() => sessionMessageSchema)).optional(),
    })),
    nextCursor: z.string().nullable().default(null),
    hasMore: z.boolean().default(false),
});
/** How far back session activity can be counted: two 28-day periods, with room to spare. */
export const SESSION_ACTIVITY_MAX_DAYS = 120;
/*
 * Session activity counts every session the caller can see that started on
 * or after `from`, so trends do not depend on how much of the session list
 * the client has loaded.
 */
export const sessionActivityParamsSchema = z.object({
    /** The earliest moment to count, in epoch milliseconds. */
    from: z.coerce.number().int().nonnegative(),
}).strict();
export const sessionActivityBucketSchema = z.object({
    /** The UTC day the sessions started, as epoch milliseconds at midnight. */
    day: z.number().int().nonnegative(),
    channelId: z.string().nullable(),
    userId: z.string().nullable(),
    sessions: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
});
export const sessionActivityResponseSchema = z.object({
    buckets: z.array(sessionActivityBucketSchema),
});
export const sessionSearchMatchKindSchema = z.enum(["title", "user", "assistant"]);
export const searchInterpretationFilterSchema = z.object({
    key: z.enum(["owner", "time", "status", "matchKind"]),
    label: z.string().trim().min(1).max(120),
});
export const searchClientSessionsParamsSchema = z.object({
    query: z.string().trim().max(500).default(""),
    channelId: z.string().trim().min(1).max(200).optional(),
    status: z.array(sessionStatusSchema).max(sessionStatusSchema.options.length).default([]),
    owner: z.enum(["me"]).optional(),
    timeZone: z.string().trim().min(1).max(100).default("UTC"),
    cursor: z.string().trim().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const sessionSearchResultSchema = z.object({
    session: sessionSummarySchema,
    matchedAt: z.number(),
    snippet: z.string().max(500),
    matchKind: sessionSearchMatchKindSchema,
    score: z.number().nonnegative(),
});
export const searchClientSessionsResponseSchema = z.object({
    results: z.array(sessionSearchResultSchema),
    nextCursor: z.string().nullable(),
    interpretation: z.object({
        text: z.string().max(500),
        filters: z.array(searchInterpretationFilterSchema),
    }),
});
export const getSessionParamsSchema = z.object({
    sessionId: sessionIdSchema,
});
export const getSessionResponseSchema = z.object({
    canWrite: z.boolean(),
    session: sessionSummarySchema.nullable(),
    sessionContract: activeSessionContractProjectionSchema.nullable().default(null),
});
export const sessionEventSchema = z.object({
    id: z.string().trim().min(1),
    sessionId: sessionIdSchema,
    kind: z.string().trim().min(1),
    payload: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.number(),
});
export const streamSessionEventsParamsSchema = z.object({
    sessionId: sessionIdSchema,
    afterEventId: z.string().min(1).optional(),
});
export const sessionEventSentinelSchema = z.object({
    sessionId: sessionIdSchema,
    status: z.enum(["cancelled", "completed", "failed"]),
});
export const streamSessionEventsEventFrameSchema = z.object({
    id: z.string().min(1),
    event: z.string().min(1),
    data: sessionEventSchema,
});
export const streamSessionEventsSentinelFrameSchema = z.object({
    id: z.string().min(1),
    event: z.literal("x-mlx-terminal-sentinel"),
    data: sessionEventSentinelSchema,
});
export const sessionMessageSchema = z.object({
    id: z.string(),
    sessionId: sessionIdSchema,
    role: z.enum(["assistant"]),
    parts: z.array(taskRunMessagePartSchema),
    status: taskRunMessageStatusSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const getSessionMessagesParamsSchema = z.object({
    sessionId: sessionIdSchema,
    paginationCursor: z.string().min(1).optional(),
});
export const getSessionMessagesResponseSchema = z.object({
    messages: z.array(sessionMessageSchema),
    continueCursor: z.string().nullable(),
    isDone: z.boolean(),
});
export const getSessionTimelineParamsSchema = z.object({
    sessionId: sessionIdSchema,
    cursor: z.string().trim().min(1).optional(),
    aroundRunId: z.string().trim().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).refine((input) => !(input.cursor && input.aroundRunId), { message: "cursor and aroundRunId cannot be combined" });
export const getSessionTimelineResponseSchema = z.object({
    turns: z.array(taskRunSummarySchema),
    nextCursor: z.string().nullable(),
    hasOlder: z.boolean(),
    hasNewer: z.boolean(),
    totalTurns: z.number().int().nonnegative(),
    windowStartIndex: z.number().int().nonnegative(),
});
export const sessionApprovalStatusSchema = z.enum([
    "pending",
    "approved",
    "rejected",
    "cancelled",
]);
export const sessionApprovalDecisionSchema = z.enum(["approved", "rejected"]);
export const sessionApprovalSchema = z.object({
    id: z.string(),
    sessionId: sessionIdSchema,
    toolCallId: z.string().trim().min(1),
    toolName: z.string().trim().min(1),
    proposedArgs: z.unknown(),
    policyReason: z.string().trim().min(1).optional(),
    status: sessionApprovalStatusSchema,
    decidedBy: z.string().optional(),
    decidedAt: z.number().optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const listSessionApprovalsParamsSchema = z.object({
    sessionId: sessionIdSchema,
    status: sessionApprovalStatusSchema.optional(),
});
export const listSessionApprovalsResponseSchema = z.object({
    approvals: z.array(sessionApprovalSchema),
});
export const listApprovalsParamsSchema = z.object({
    status: sessionApprovalStatusSchema.default("pending"),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const listApprovalsResponseSchema = z.object({
    approvals: z.array(sessionApprovalSchema),
});
export const submitSessionApprovalInputSchema = z.object({
    sessionId: sessionIdSchema,
    approvalId: z.string().trim().min(1),
    decision: sessionApprovalDecisionSchema,
    comment: z.string().trim().min(1).optional(),
});
export const submitExecutionSessionApprovalInputSchema = submitSessionApprovalInputSchema
    .extend({ approvalAssertion: approvalDecisionAssertionV1Schema })
    .strict();
export const cancelSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
    reason: z.string().trim().min(1).optional(),
    /** Withdraw only this message still queued behind a working turn. */
    queuedRunId: z.string().trim().min(1).optional(),
});
/**
 * Roll a session back to a finished turn: the next message continues from it,
 * and the turns after it leave the conversation's context. Nothing is deleted;
 * the rolled-back turns stay in the session's history.
 */
export const rewindSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
    throughRunId: z.string().trim().min(1),
}).strict();
/** The latest rollback, kept in the session's metadata as `mlxRewind`. */
export const sessionRewindSchema = z.object({
    throughRunId: z.string().min(1),
    rewoundAt: z.number(),
});
export const archiveSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
});
export const patchSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
    title: z.string().trim().min(1).max(200),
});
export const patchSessionResponseSchema = z.object({
    session: sessionSummarySchema,
});
export const deleteSessionInputSchema = z.object({
    sessionId: sessionIdSchema,
});
